import { useAdminPortalConfig } from 'api/adminPortalConfig';
import { useProjectWidgets } from 'api/projectWidgets';
import { ROUTES, Segments } from 'app/Views/routes';
import { uniq } from 'lodash';
import { useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { useIsUserAdmin, useTenant } from 'state/user';
import { ConfigWidget } from 'types/adminPortalConfig';
import { Widget, WidgetType } from 'types/widgets';

const ADMIN_ONLY_WIDGET_TYPES = new Set<WidgetType>([
	WidgetType.USER_MANAGEMENT,
	WidgetType.ROLE_MANAGEMENT,
	WidgetType.ACCESS_KEY_MANAGEMENT,
	WidgetType.AUDIT_MANAGEMENT,
	WidgetType.TENANT_PROFILE
]);

export const TENANT_SCOPED_WIDGET_TYPES = new Set<WidgetType>([
	WidgetType.USER_MANAGEMENT,
	WidgetType.ROLE_MANAGEMENT,
	WidgetType.ACCESS_KEY_MANAGEMENT,
	WidgetType.AUDIT_MANAGEMENT,
	WidgetType.TENANT_PROFILE,
	WidgetType.OUTBOUND_APPLICATIONS
]);

const widgetNameKeys: Record<WidgetType, string> = {
	[WidgetType.APPLICATIONS_PORTAL]: 'widget.applicationsPortal',
	[WidgetType.OUTBOUND_APPLICATIONS]: 'widget.outboundApplications',
	[WidgetType.USER_PROFILE]: 'widget.userProfile',
	[WidgetType.ACCESS_KEY_MANAGEMENT]: 'widget.accessKeyManagement',
	[WidgetType.AUDIT_MANAGEMENT]: 'widget.auditLog',
	[WidgetType.ROLE_MANAGEMENT]: 'widget.rolesManagement',
	[WidgetType.TENANT_PROFILE]: 'widget.tenantProfile',
	[WidgetType.USER_MANAGEMENT]: 'widget.userManagement'
};

export const useWidgets = (): Widget[] | null => {
	const { data: appConfig } = useAdminPortalConfig();
	const { data: projectWidgetsData } = useProjectWidgets();
	const isUserAdmin = useIsUserAdmin();
	const { tenant } = useTenant();

	return useMemo(() => {
		const tenantPermissions = tenant?.permissions ?? [];
		if (appConfig?.widgets && projectWidgetsData?.widgets.length) {
			const widgetTypes = appConfig.widgets
				.map((widget) => widget.type)
				.filter(Boolean);

			const invalidWidgetTypes = uniq(
				widgetTypes.filter(
					(widgetType) => !Object.values(WidgetType).includes(widgetType)
				)
			);
			if (invalidWidgetTypes.length > 0) {
				/* istanbul ignore next  */
				// eslint-disable-next-line no-console
				console.warn(
					`Unsupported widgetTypes: ${invalidWidgetTypes.join(', ')}.`
				);
			}

			const projectWidgetsInfo = Object.fromEntries(
				projectWidgetsData.widgets.map((widget) => [widget.widgetId, widget])
			);
			const missingWidgets = appConfig.widgets.filter(
				(widget) => !projectWidgetsInfo[widget.id]
			);
			if (missingWidgets.length > 0) {
				/* istanbul ignore next  */
				// eslint-disable-next-line no-console
				console.info(
					`Missing widgets: ${missingWidgets.map((widget) => widget.id).join(', ')}.`
				);
			}
			const disabledWidgets = appConfig.widgets.filter(
				(widget) => projectWidgetsInfo[widget.id]?.disabled
			);
			if (disabledWidgets.length > 0) {
				/* istanbul ignore next  */
				// eslint-disable-next-line no-console
				console.info(
					`Disabled widgets: ${disabledWidgets.map((widget) => widget.id).join(', ')}.`
				);
			}

			const widgets = appConfig.widgets
				.filter(
					(widgetConfig) =>
						!invalidWidgetTypes.includes(widgetConfig.type) &&
						projectWidgetsInfo[widgetConfig.id] &&
						!projectWidgetsInfo[widgetConfig.id].disabled &&
						(isUserAdmin || !ADMIN_ONLY_WIDGET_TYPES.has(widgetConfig.type)) &&
						(tenant || !TENANT_SCOPED_WIDGET_TYPES.has(widgetConfig.type)) &&
						(
							projectWidgetsInfo[widgetConfig.id]?.requiredPermissions ?? []
						).every((permission) => tenantPermissions.includes(permission))
				)
				.map((widgetConfig) => ({
					id: widgetConfig.id,
					name:
						projectWidgetsInfo[widgetConfig.id]?.name ||
						intl.tr(widgetNameKeys[widgetConfig.type]),
					path: widgetConfig.id,
					type: widgetConfig.type
				}));

			return widgets;
		}

		return null;
	}, [appConfig?.widgets, projectWidgetsData?.widgets, isUserAdmin, tenant]);
};

export const useSelectedWidgetByRoute = (): ConfigWidget | null => {
	const match = useMatch(ROUTES.widget);
	const widgets = useWidgets();

	return useMemo(() => {
		if (!widgets || !match?.params[Segments.WidgetPath]) {
			return null;
		}
		return (
			widgets.find((w) => w.path === match.params[Segments.WidgetPath]) || null
		);
	}, [match, widgets]);
};
